import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaPlus, FaTimes, FaChevronDown, FaChevronRight, FaPencilAlt, FaUserMinus, FaUserCheck, FaTrashAlt,
  FaDownload, FaExclamationTriangle,
} from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/campamentos";
const IGLESIA_PROPIA = "Monte de los Olivos";
// Valor especial del selector de tarifa: la cuota se escribe a mano para ese asistente (p. ej. según los días).
const PERSONALIZADA = "__personalizada__";

const ESTATUS = {
  pagado: { etiqueta: "Pagado", estilo: "bg-emerald-50 text-emerald-700" },
  pendiente: { etiqueta: "Pendiente", estilo: "bg-amber-50 text-amber-700" },
  becado: { etiqueta: "Becado", estilo: "bg-sky-50 text-sky-700" },
};
const FORMAS_PAGO = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "otro", label: "Otro" },
];

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Las fechas llegan como YYYY-MM-DD (sin zona horaria): se formatean sin pasar por Date.
const formatoFecha = (fecha) => {
  if (!fecha) return "";
  const [anio, mes, dia] = fecha.split("-");
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${dia} ${meses[Number(mes) - 1]} ${anio}`;
};

const hoyMexico = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(new Date());

const clasesInput =
  "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none";
const clasesBotonPrimario =
  "px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50";
const clasesBotonSecundario =
  "px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-50";

// Encabezado de una sección que se puede abrir y cerrar; cerrada, muestra un resumen de lo capturado.
function EncabezadoSeccion({ titulo, ayuda, abierto, onToggle, resumen }) {
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={abierto}
        className="w-full flex items-center gap-2 text-left group"
      >
        <span className="text-slate-400 group-hover:text-slate-600">
          {abierto ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
        </span>
        <span className="text-sm font-semibold text-slate-800">{titulo}</span>
        {!abierto && resumen && <span className="text-xs text-slate-500 truncate">· {resumen}</span>}
        {!abierto && <span className="ml-auto text-xs text-indigo-600 group-hover:text-indigo-800 shrink-0">Mostrar</span>}
        {abierto && <span className="ml-auto text-xs text-slate-400 group-hover:text-slate-600 shrink-0">Ocultar</span>}
      </button>
      {abierto && ayuda && <p className="text-xs text-slate-400 mt-1 ml-5">{ayuda}</p>}
    </div>
  );
}

let contadorClaves = 0;
const nuevaClave = () => `k${++contadorClaves}`;

const FORM_VACIO = { nombre: "", tema: "", fecha_inicio: "", fecha_fin: "" };

// Llamada a la API: devuelve { ok, data, mensaje } sin lanzar excepciones.
async function llamar(ruta, { method = "GET", cuerpo } = {}) {
  try {
    const res = await fetch(`${API}${ruta}`, {
      method,
      credentials: "include",
      headers: cuerpo !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data, mensaje: data.mensaje || (res.ok ? "" : "Ocurrió un error") };
  } catch (error) {
    console.error(error);
    return { ok: false, data: {}, mensaje: "No se pudo conectar con el servidor" };
  }
}

// Selector de un miembro (búsqueda por nombre).
function SelectorMiembro({ miembros, seleccionado, onChange }) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef(null);

  useEffect(() => {
    const cerrar = (e) => { if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", cerrar);
    return () => document.removeEventListener("mousedown", cerrar);
  }, []);

  const sugerencias = useMemo(() => {
    const b = texto.trim().toLowerCase();
    return miembros.filter((m) => !b || m.nombre.toLowerCase().includes(b)).slice(0, 8);
  }, [miembros, texto]);

  if (seleccionado) {
    return (
      <div className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-indigo-50/40">
        <span className="truncate text-slate-700">{seleccionado.nombre}</span>
        <button type="button" onClick={() => onChange(null)} aria-label="Quitar miembro" className="text-slate-400 hover:text-slate-600">
          <FaTimes className="text-xs" />
        </button>
      </div>
    );
  }

  return (
    <div ref={contenedor} className="relative">
      <input
        type="text"
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onClick={() => setAbierto(true)}
        placeholder="Buscar miembro..."
        className={clasesInput}
      />
      {abierto && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
          {sugerencias.length === 0 ? (
            <li className="px-3 py-2 text-slate-400">Sin coincidencias</li>
          ) : (
            sugerencias.map((m) => (
              <li key={m.id_miembro}>
                <button
                  type="button"
                  onClick={() => { onChange(m); setTexto(""); setAbierto(false); }}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
                >
                  {m.nombre}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// Formulario para agregar o editar a un asistente. `tarifas` = [{ valor, nombre, monto }].
function FormAsistente({ miembros, tarifas, inicial, editando, onGuardar, onCancelar, guardando }) {
  // Al agregar se propone "Miembro" (lo más común); las personas de otras iglesias se eligen con "Otra persona".
  const [esMiembro, setEsMiembro] = useState(editando ? !!inicial?.id_miembro : true);
  const [miembro, setMiembro] = useState(null);
  const [nombre, setNombre] = useState(inicial?.nombre || "");
  const [iglesia, setIglesia] = useState(inicial?.iglesia || "");
  const [tarifa, setTarifa] = useState(inicial?.tarifa ?? "");
  const [beca, setBeca] = useState(inicial?.beca ? String(inicial.beca) : "");
  const [formulario, setFormulario] = useState(!!inicial?.formulario_firmado);
  const [cuotaPers, setCuotaPers] = useState(inicial?.cuota_personalizada ? String(inicial.cuota_personalizada) : "");
  const [detalleCuota, setDetalleCuota] = useState(inicial?.detalle_cuota || "");
  const [error, setError] = useState("");

  const esPersonalizada = tarifa === PERSONALIZADA;
  const cuota = esPersonalizada
    ? (Number(cuotaPers) > 0 ? Number(cuotaPers) : undefined)
    : tarifas.find((t) => String(t.valor) === String(tarifa))?.monto;

  const elegirMiembro = (m) => {
    setMiembro(m);
    if (m && !iglesia) setIglesia(IGLESIA_PROPIA);
  };

  const enviar = () => {
    if (!editando && esMiembro && !miembro) return setError("Elige al miembro");
    if (!(esMiembro && !editando) && !nombre.trim()) return setError("Escribe el nombre");
    if (tarifa === "") return setError("Elige la tarifa o el monto personalizado");
    if (esPersonalizada && !(Number(cuotaPers) > 0)) return setError("Escribe la cuota individual (mayor a 0)");
    const becaNum = beca === "" ? 0 : Number(beca);
    if (Number.isNaN(becaNum) || becaNum < 0) return setError("La beca no es válida");
    if (cuota !== undefined && becaNum > cuota) return setError(`La beca no puede ser mayor que la cuota (${formatoMoneda(cuota)})`);
    setError("");
    onGuardar({
      nombre: nombre.trim(),
      id_miembro: !editando && esMiembro ? miembro.id_miembro : null,
      iglesia: iglesia.trim(),
      tarifa,
      cuota_personalizada: esPersonalizada ? Number(cuotaPers) : null,
      detalle_cuota: esPersonalizada ? detalleCuota.trim() : "",
      formulario_firmado: formulario,
      beca: becaNum,
    });
  };

  return (
    <div className="border border-indigo-100 bg-indigo-50/30 rounded-lg p-3 space-y-3">
      {!editando && (
        <div className="flex gap-2 text-xs">
          {[["Miembro de la iglesia", true], ["Otra persona", false]].map(([texto, valor]) => (
            <button
              key={texto}
              type="button"
              onClick={() => { setEsMiembro(valor); setMiembro(null); }}
              className={`px-3 py-1 rounded-full border font-medium transition ${
                esMiembro === valor ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {texto}
            </button>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            {!editando && esMiembro ? "Miembro" : "Nombre"} <span className="text-rose-500">*</span>
          </label>
          {!editando && esMiembro ? (
            <SelectorMiembro miembros={miembros} seleccionado={miembro} onChange={elegirMiembro} />
          ) : (
            <input type="text" maxLength={120} value={nombre} onChange={(e) => setNombre(e.target.value)} className={clasesInput} />
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Iglesia a la que pertenece</label>
          <input type="text" maxLength={120} value={iglesia} onChange={(e) => setIglesia(e.target.value)} placeholder="Ej. Iglesia Bautista Ebenezer" className={clasesInput} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Tarifa <span className="text-rose-500">*</span></label>
          <select value={tarifa} onChange={(e) => setTarifa(e.target.value)} className={clasesInput}>
            <option value="">-- Selecciona --</option>
            {tarifas.map((t) => <option key={t.valor} value={t.valor}>{t.nombre} · {formatoMoneda(t.monto)}</option>)}
            <option value={PERSONALIZADA}>Monto personalizado (individual)…</option>
          </select>
        </div>
        {esPersonalizada && (
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3 rounded-lg bg-white border border-indigo-100 p-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Cuota individual <span className="text-rose-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" min="0" step="0.01" value={cuotaPers} onChange={(e) => setCuotaPers(e.target.value)} placeholder="0.00" className={`${clasesInput} pl-6`} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Detalle (opcional)</label>
              <input type="text" maxLength={100} value={detalleCuota} onChange={(e) => setDetalleCuota(e.target.value)} placeholder="Ej. Solo 2 días" className={clasesInput} />
            </div>
            <p className="sm:col-span-2 text-xs text-slate-400">Úsalo cuando el monto de esta persona es distinto al de las tarifas (por ejemplo, por los días que asistirá).</p>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Beca (si se le regala una parte)</label>
          <input type="number" min="0" step="0.01" value={beca} onChange={(e) => setBeca(e.target.value)} placeholder="0.00" className={clasesInput} />
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={formulario} onChange={(e) => setFormulario(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
        Entregó su formulario firmado
      </label>

      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancelar} className={clasesBotonSecundario}>Cancelar</button>
        <button type="button" onClick={enviar} disabled={guardando} className={clasesBotonPrimario}>
          {guardando ? "Guardando..." : editando ? "Guardar asistente" : "Agregar asistente"}
        </button>
      </div>
    </div>
  );
}

export default function CampamentoModal({ open, idCampamento, miembros, onClose, onChanged }) {
  const [idActual, setIdActual] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [tarifas, setTarifas] = useState([]);
  const [borradores, setBorradores] = useState([]); // asistentes por agregar (solo al crear)
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoAsistente, setEditandoAsistente] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [verBajas, setVerBajas] = useState(false);
  const [soloSinFormulario, setSoloSinFormulario] = useState(false);
  const [filtroEstatus, setFiltroEstatus] = useState(""); // "" = todos, o pagado / pendiente / becado
  const [abonoForm, setAbonoForm] = useState({ monto: "", fecha: "", forma_pago: "efectivo", nota: "" });
  // "Datos" y "Tarifas" se ven abiertos cuando están vacíos (campamento nuevo) y cerrados cuando ya tienen información.
  const [datosAbierto, setDatosAbierto] = useState(true);
  const [tarifasAbierto, setTarifasAbierto] = useState(true);
  const [bajaPendiente, setBajaPendiente] = useState(null);
  const [abonoACancelar, setAbonoACancelar] = useState(null);

  const esNuevo = idActual === null;

  const cargarDetalle = async (id) => {
    setCargando(true);
    const r = await llamar(`/${id}`);
    setCargando(false);
    if (!r.ok) { setError(r.mensaje); return; }
    aplicarDetalle(r.data, true);
  };

  const aplicarDetalle = (d, reiniciarFormulario) => {
    setDetalle(d);
    if (reiniciarFormulario) {
      setDatosAbierto(false);
      setTarifasAbierto(false);
      setForm({ nombre: d.nombre, tema: d.tema || "", fecha_inicio: d.fecha_inicio, fecha_fin: d.fecha_fin || "" });
      setTarifas(d.tarifas.map((t) => ({ clave: String(t.id_tarifa), id_tarifa: t.id_tarifa, nombre: t.nombre, monto: String(t.monto) })));
    }
  };

  // Al abrir: campamento nuevo (vacío) o existente (se carga con sus asistentes y abonos).
  useEffect(() => {
    if (!open) return;
    setError(""); setMostrarForm(false); setEditandoAsistente(null); setExpandido(null); setVerBajas(false);
    setBorradores([]); setDetalle(null);
    setFiltroEstatus(""); setSoloSinFormulario(false);
    setIdActual(idCampamento ?? null);
    if (idCampamento) {
      cargarDetalle(idCampamento);
    } else {
      setForm(FORM_VACIO);
      setDatosAbierto(true);
      setTarifasAbierto(true);
      setTarifas([{ clave: nuevaClave(), id_tarifa: null, nombre: "Campero", monto: "" }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idCampamento]);

  // ---------- tarifas ----------
  const cambiarTarifa = (clave, campo, valor) => setTarifas((lista) => lista.map((t) => (t.clave === clave ? { ...t, [campo]: valor } : t)));
  const agregarTarifa = () => setTarifas((lista) => [...lista, { clave: nuevaClave(), id_tarifa: null, nombre: "", monto: "" }]);
  const quitarTarifa = (t) => {
    const usada = esNuevo
      ? borradores.some((b) => b.tarifa === t.clave)
      : (detalle?.asistentes || []).some((a) => a.activo && a.id_tarifa === t.id_tarifa);
    if (usada) return setError(`No puedes quitar la tarifa "${t.nombre}": hay asistentes que la usan. Cámbialos de tarifa primero.`);
    setError("");
    setTarifas((lista) => lista.filter((x) => x.clave !== t.clave));
  };

  const cuerpoCampamento = () => ({
    nombre: form.nombre,
    tema: form.tema,
    fecha_inicio: form.fecha_inicio,
    fecha_fin: form.fecha_fin || null,
    tarifas: tarifas.map((t) => ({ id_tarifa: t.id_tarifa, nombre: t.nombre, monto: t.monto })),
  });

  const validarCampamento = () => {
    if (!form.nombre.trim()) return "Escribe el nombre del campamento";
    if (!form.fecha_inicio) return "Indica la fecha del campamento";
    if (tarifas.length === 0) return "Agrega al menos una tarifa";
    if (tarifas.some((t) => !t.nombre.trim() || !(Number(t.monto) > 0))) return "Cada tarifa necesita nombre y un monto mayor a 0";
    return "";
  };

  // ---------- guardar campamento ----------
  const guardarCampamento = async () => {
    const problema = validarCampamento();
    if (problema) {
      // Si falta algo, se abren las secciones para que se vea qué completar.
      setDatosAbierto(true);
      setTarifasAbierto(true);
      return setError(problema);
    }
    setError("");
    setGuardando(true);

    if (esNuevo) {
      const cuerpo = {
        ...cuerpoCampamento(),
        asistentes: borradores.map((b) => ({
          nombre: b.nombre, id_miembro: b.id_miembro, iglesia: b.iglesia, beca: b.beca,
          tarifa_index: b.tarifa === PERSONALIZADA ? null : tarifas.findIndex((t) => t.clave === b.tarifa),
          cuota_personalizada: b.cuota_personalizada, detalle_cuota: b.detalle_cuota, formulario_firmado: !!b.formulario_firmado,
        })),
      };
      const r = await llamar("", { method: "POST", cuerpo });
      setGuardando(false);
      if (!r.ok) return setError(r.mensaje);
      // Queda abierto en modo edición para poder registrar abonos.
      setIdActual(r.data.id_campamento);
      setBorradores([]);
      aplicarDetalle(r.data, true);
      onChanged?.("creado");
    } else {
      const r = await llamar(`/${idActual}`, { method: "PUT", cuerpo: cuerpoCampamento() });
      setGuardando(false);
      if (!r.ok) return setError(r.mensaje);
      aplicarDetalle(r.data, true);
      onChanged?.("editado");
    }
  };

  // ---------- asistentes ----------
  const tarifasParaElegir = esNuevo
    ? tarifas.filter((t) => t.nombre.trim() && Number(t.monto) > 0).map((t) => ({ valor: t.clave, nombre: t.nombre, monto: Number(t.monto) }))
    : (detalle?.tarifas || []).map((t) => ({ valor: t.id_tarifa, nombre: t.nombre, monto: t.monto }));

  const agregarAsistente = async (datos) => {
    if (esNuevo) {
      setBorradores((lista) => [...lista, { clave: nuevaClave(), ...datos, nombre: datos.nombre || datos.nombreMiembro }]);
      setMostrarForm(false);
      return;
    }
    setGuardando(true);
    const r = await llamar(`/${idActual}/asistentes`, {
      method: "POST",
      cuerpo: {
        nombre: datos.nombre, id_miembro: datos.id_miembro, iglesia: datos.iglesia, beca: datos.beca,
        id_tarifa: datos.tarifa === PERSONALIZADA ? null : datos.tarifa,
        cuota_personalizada: datos.cuota_personalizada, detalle_cuota: datos.detalle_cuota,
        formulario_firmado: datos.formulario_firmado,
      },
    });
    setGuardando(false);
    if (!r.ok) return setError(r.mensaje);
    setError("");
    aplicarDetalle(r.data, false);
    setMostrarForm(false);
    onChanged?.();
  };

  const guardarEdicionAsistente = async (datos) => {
    setGuardando(true);
    const r = await llamar(`/asistentes/${editandoAsistente.id_asistente}`, {
      method: "PUT",
      cuerpo: {
        nombre: datos.nombre, iglesia: datos.iglesia, beca: datos.beca,
        id_tarifa: datos.tarifa === PERSONALIZADA ? null : datos.tarifa,
        cuota_personalizada: datos.cuota_personalizada, detalle_cuota: datos.detalle_cuota,
        formulario_firmado: datos.formulario_firmado,
      },
    });
    setGuardando(false);
    if (!r.ok) return setError(r.mensaje);
    setError("");
    aplicarDetalle(r.data, false);
    setEditandoAsistente(null);
    onChanged?.();
  };

  const cambiarEstatusAsistente = async (asistente, activar) => {
    const r = await llamar(`/asistentes/${asistente.id_asistente}${activar ? "/reactivar" : ""}`, { method: activar ? "PATCH" : "DELETE" });
    setBajaPendiente(null);
    if (!r.ok) return setError(r.mensaje);
    setError("");
    aplicarDetalle(r.data, false);
    onChanged?.();
  };

  // ---------- abonos ----------
  const expandir = (asistente) => {
    if (expandido === asistente.id_asistente) return setExpandido(null);
    setExpandido(asistente.id_asistente);
    setEditandoAsistente(null);
    setAbonoForm({ monto: "", fecha: hoyMexico(), forma_pago: "efectivo", nota: "" });
  };

  const registrarAbono = async (asistente) => {
    const monto = Number(abonoForm.monto);
    if (!(monto > 0)) return setError("Escribe el monto del abono");
    if (monto > asistente.saldo + 0.001) return setError(`El abono supera el saldo pendiente (${formatoMoneda(asistente.saldo)})`);
    setError("");
    setGuardando(true);
    const r = await llamar(`/asistentes/${asistente.id_asistente}/abonos`, { method: "POST", cuerpo: abonoForm });
    setGuardando(false);
    if (!r.ok) return setError(r.mensaje);
    aplicarDetalle(r.data, false);
    setAbonoForm({ monto: "", fecha: hoyMexico(), forma_pago: "efectivo", nota: "" });
    onChanged?.();
  };

  // Marca o desmarca "entregó formulario firmado" directo desde la tabla.
  const alternarFormulario = async (asistente, firmado) => {
    const r = await llamar(`/asistentes/${asistente.id_asistente}/formulario`, { method: "PATCH", cuerpo: { firmado } });
    if (!r.ok) return setError(r.mensaje);
    setError("");
    aplicarDetalle(r.data, false);
    onChanged?.();
  };

  const cancelarAbono = async () => {
    const r = await llamar(`/abonos/${abonoACancelar.id_abono}`, { method: "DELETE" });
    setAbonoACancelar(null);
    if (!r.ok) return setError(r.mensaje);
    setError("");
    aplicarDetalle(r.data, false);
    onChanged?.();
  };

  // ---------- exportar lista de asistentes ----------
  const exportarAsistentes = () => {
    const celda = (v) => {
      let t = String(v ?? "");
      if (/^[=+\-@\t\r]/.test(t)) t = `'${t}`;
      return `"${t.replace(/"/g, '""')}"`;
    };
    const lineas = [["#", "Nombre", "Iglesia", "Tarifa", "Cuota", "Beca", "Abonado", "Saldo", "Estatus", "Formulario firmado"].map(celda).join(",")];
    (detalle?.asistentes || []).filter((a) => a.activo).forEach((a, i) => {
      lineas.push([i + 1, a.nombre, a.iglesia || "", `${a.tarifa}${a.detalle_cuota ? ` (${a.detalle_cuota})` : ""}`, a.cuota, a.beca, a.abonado, a.saldo, ESTATUS[a.estatus].etiqueta, a.formulario_firmado ? "Sí" : "No"].map(celda).join(","));
    });
    const blob = new Blob(["﻿" + lineas.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asistentes_${(detalle?.nombre || "campamento").replace(/[^a-z0-9]+/gi, "_")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resumenDatos = [
    form.nombre,
    form.tema,
    form.fecha_inicio ? (form.fecha_fin && form.fecha_fin !== form.fecha_inicio ? `${formatoFecha(form.fecha_inicio)} – ${formatoFecha(form.fecha_fin)}` : formatoFecha(form.fecha_inicio)) : "",
  ].filter(Boolean).join(" · ");
  const resumenTarifas = tarifas
    .filter((t) => t.nombre.trim())
    .map((t) => `${t.nombre.trim()} ${Number(t.monto) > 0 ? formatoMoneda(t.monto) : ""}`.trim())
    .join(" · ");

  const asistentesVisibles = (detalle?.asistentes || []).filter(
    (a) => (verBajas || a.activo)
      && (!soloSinFormulario || (a.activo && !a.formulario_firmado))
      && (!filtroEstatus || a.estatus === filtroEstatus)
  );
  // Cuántos asistentes activos hay en cada estatus (para mostrarlo en el selector).
  const cuentaPorEstatus = (detalle?.asistentes || []).filter((a) => a.activo).reduce((c, a) => ({ ...c, [a.estatus]: (c[a.estatus] || 0) + 1 }), {});
  const hayFiltros = !!filtroEstatus || soloSinFormulario;
  const bajas = (detalle?.asistentes || []).filter((a) => !a.activo).length;
  const inactivo = detalle && !detalle.activo;
  const resumen = detalle?.resumen;

  const tarjeta = (titulo, valor, estilo = "text-slate-800") => (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-0.5">{titulo}</p>
      <p className={`text-base font-bold truncate ${estilo}`}>{valor}</p>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={esNuevo ? "Nuevo campamento" : `Campamento: ${detalle?.nombre || ""}`}
        maxWidth="max-w-5xl"
      >
        {cargando ? (
          <p className="text-center text-slate-400 py-10">Cargando...</p>
        ) : (
          <div className="space-y-6">

            {inactivo && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Este campamento está inactivo: se puede consultar, pero no modificar. Reactívalo desde la lista.
              </p>
            )}

            {/* RESUMEN */}
            {!esNuevo && resumen && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {tarjeta("Debería recaudarse", formatoMoneda(resumen.deberia))}
                {tarjeta("Monto recaudado", formatoMoneda(resumen.recaudado), "text-emerald-600")}
                {tarjeta("Monto pendiente", formatoMoneda(resumen.pendiente), resumen.pendiente > 0 ? "text-amber-600" : "text-slate-800")}
                {tarjeta("Beca otorgada", formatoMoneda(resumen.becas), "text-sky-600")}
                {tarjeta("Formularios firmados", `${resumen.formularios} de ${resumen.asistentes}`, resumen.formularios < resumen.asistentes ? "text-amber-600" : "text-emerald-600")}
              </div>
            )}

            {/* DATOS */}
            <section>
              <EncabezadoSeccion titulo="Datos del campamento" abierto={datosAbierto} onToggle={() => setDatosAbierto(!datosAbierto)} resumen={resumenDatos} />
              {datosAbierto && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Campamento <span className="text-rose-500">*</span></label>
                  <input type="text" maxLength={120} value={form.nombre} disabled={inactivo} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Campamento 2026" className={clasesInput} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Tema</label>
                  <input type="text" maxLength={200} value={form.tema} disabled={inactivo} onChange={(e) => setForm({ ...form, tema: e.target.value })} className={clasesInput} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Fecha de inicio <span className="text-rose-500">*</span></label>
                  <input type="date" value={form.fecha_inicio} disabled={inactivo} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className={clasesInput} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Fecha de término (opcional)</label>
                  <input type="date" value={form.fecha_fin} min={form.fecha_inicio || undefined} disabled={inactivo} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className={clasesInput} />
                </div>
              </div>
              )}
            </section>

            {/* TARIFAS */}
            <section>
              <EncabezadoSeccion
                titulo="Tarifas"
                ayuda="El monto se configura una sola vez por tipo de asistente (campero, niño, colaborador...)."
                abierto={tarifasAbierto}
                onToggle={() => setTarifasAbierto(!tarifasAbierto)}
                resumen={resumenTarifas}
              />
              {tarifasAbierto && (
              <>
              <div className="space-y-2">
                {tarifas.map((t) => (
                  <div key={t.clave} className="flex items-center gap-2">
                    <input type="text" maxLength={60} value={t.nombre} disabled={inactivo} onChange={(e) => cambiarTarifa(t.clave, "nombre", e.target.value)} placeholder="Tipo (Campero, Niño...)" className={`${clasesInput} flex-1`} />
                    <div className="relative w-40">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input type="number" min="0" step="0.01" value={t.monto} disabled={inactivo} onChange={(e) => cambiarTarifa(t.clave, "monto", e.target.value)} placeholder="0.00" className={`${clasesInput} pl-6`} />
                    </div>
                    {!inactivo && (
                      <button type="button" onClick={() => quitarTarifa(t)} aria-label={`Quitar tarifa ${t.nombre}`} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition">
                        <FaTrashAlt className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {!inactivo && (
                <button type="button" onClick={agregarTarifa} className="mt-2 inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800">
                  <FaPlus className="text-xs" /> Agregar tarifa
                </button>
              )}
              </>
              )}
            </section>

            {/* ASISTENTES */}
            <section>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-slate-800">
                  Asistentes <span className="font-normal text-slate-400">({esNuevo ? borradores.length : resumen?.asistentes || 0})</span>
                </h3>
                <div className="flex items-center gap-2">
                  {!esNuevo && (resumen?.asistentes || 0) > 0 && (
                    <button type="button" onClick={exportarAsistentes} className={`${clasesBotonSecundario} inline-flex items-center gap-2`}>
                      <FaDownload className="text-xs" /> Exportar lista
                    </button>
                  )}
                  {!inactivo && (
                    <button
                      type="button"
                      onClick={() => { setMostrarForm(true); setEditandoAsistente(null); }}
                      disabled={tarifasParaElegir.length === 0}
                      title={tarifasParaElegir.length === 0 ? "Primero agrega al menos una tarifa" : ""}
                      className={`${clasesBotonPrimario} inline-flex items-center gap-2`}
                    >
                      <FaPlus className="text-xs" /> Agregar asistente
                    </button>
                  )}
                </div>
              </div>

              {!esNuevo && !inactivo && (
                <p className="text-xs text-slate-400 mb-2">
                  Los asistentes y los abonos se guardan al momento. Los datos del campamento y las tarifas, con "Guardar cambios".
                </p>
              )}

              {mostrarForm && (
                <div className="mb-3">
                  <FormAsistente
                    miembros={miembros}
                    tarifas={tarifasParaElegir}
                    guardando={guardando}
                    onCancelar={() => setMostrarForm(false)}
                    onGuardar={(datos) => agregarAsistente({
                      ...datos,
                      nombreMiembro: datos.id_miembro ? miembros.find((m) => m.id_miembro === datos.id_miembro)?.nombre : "",
                      iglesia: datos.iglesia || (datos.id_miembro ? IGLESIA_PROPIA : ""),
                    })}
                  />
                </div>
              )}

              {/* Alta de campamento: lista provisional */}
              {esNuevo && (
                borradores.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                    Aún no hay asistentes. Puedes agregarlos ahora o después de crear el campamento.
                  </p>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Nombre</th>
                          <th className="px-3 py-2 text-left font-semibold">Iglesia</th>
                          <th className="px-3 py-2 text-left font-semibold">Tarifa</th>
                          <th className="px-3 py-2 text-right font-semibold">Beca</th>
                          <th className="px-3 py-2 text-center font-semibold">Formulario</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {borradores.map((b) => {
                          const t = tarifasParaElegir.find((x) => x.valor === b.tarifa);
                          const textoTarifa = b.tarifa === PERSONALIZADA
                            ? `Personalizada · ${formatoMoneda(b.cuota_personalizada)}${b.detalle_cuota ? ` (${b.detalle_cuota})` : ""}`
                            : t ? `${t.nombre} · ${formatoMoneda(t.monto)}` : "—";
                          return (
                            <tr key={b.clave} className="border-t border-slate-100">
                              <td className="px-3 py-2 text-slate-800">{b.nombre}{b.id_miembro && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">Miembro</span>}</td>
                              <td className="px-3 py-2 text-slate-600">{b.iglesia || "—"}</td>
                              <td className="px-3 py-2 text-slate-600">{textoTarifa}</td>
                              <td className="px-3 py-2 text-right text-slate-600">{formatoMoneda(b.beca)}</td>
                              <td className="px-3 py-2 text-center text-slate-600">{b.formulario_firmado ? "Sí" : "No"}</td>
                              <td className="px-3 py-2 text-right">
                                <button type="button" onClick={() => setBorradores((l) => l.filter((x) => x.clave !== b.clave))} aria-label={`Quitar a ${b.nombre}`} className="w-7 h-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                  <FaTimes className="text-xs" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Filtros de la lista de asistentes */}
              {!esNuevo && (resumen?.asistentes || 0) > 0 && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3">
                  <div className="flex items-center gap-2">
                    <label htmlFor="filtro-estatus-asistentes" className="text-xs text-slate-500">Estatus</label>
                    <select
                      id="filtro-estatus-asistentes"
                      value={filtroEstatus}
                      onChange={(e) => setFiltroEstatus(e.target.value)}
                      className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    >
                      <option value="">Todos ({resumen.asistentes})</option>
                      {Object.entries(ESTATUS).map(([clave, e]) => (
                        <option key={clave} value={clave}>{e.etiqueta} ({cuentaPorEstatus[clave] || 0})</option>
                      ))}
                    </select>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                    <input type="checkbox" checked={soloSinFormulario} onChange={(e) => setSoloSinFormulario(e.target.checked)} />
                    Mostrar solo a quienes les falta el formulario
                  </label>
                  {hayFiltros && (
                    <button type="button" onClick={() => { setFiltroEstatus(""); setSoloSinFormulario(false); }} className="text-xs text-indigo-600 hover:text-indigo-800">
                      Quitar filtros
                    </button>
                  )}
                </div>
              )}

              {/* Campamento existente: asistentes con abonos */}
              {!esNuevo && (
                asistentesVisibles.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                    {hayFiltros && (resumen?.asistentes || 0) > 0 ? "Ningún asistente coincide con los filtros." : "Todavía no hay asistentes en este campamento."}
                  </p>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="px-2 py-2 w-8" />
                          <th className="px-3 py-2 text-left font-semibold">#</th>
                          <th className="px-3 py-2 text-left font-semibold">Nombre</th>
                          <th className="px-3 py-2 text-left font-semibold">Iglesia</th>
                          <th className="px-3 py-2 text-left font-semibold">Tarifa</th>
                          <th className="px-3 py-2 text-right font-semibold">Cuota</th>
                          <th className="px-3 py-2 text-right font-semibold">Beca</th>
                          <th className="px-3 py-2 text-right font-semibold">Abonado</th>
                          <th className="px-3 py-2 text-right font-semibold">Saldo</th>
                          <th className="px-3 py-2 text-left font-semibold">Estatus</th>
                          <th className="px-3 py-2 text-center font-semibold" title="Entregó su formulario firmado">Formulario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asistentesVisibles.map((a, i) => {
                          const abierto = expandido === a.id_asistente;
                          const est = ESTATUS[a.estatus];
                          return (
                            <FilaAsistente key={a.id_asistente}>
                              <tr
                                onClick={() => expandir(a)}
                                className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition ${!a.activo ? "opacity-60" : ""} ${abierto ? "bg-slate-50" : ""}`}
                              >
                                <td className="px-2 py-2 text-slate-400">{abierto ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}</td>
                                <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                                <td className="px-3 py-2 text-slate-800 font-medium">
                                  {a.nombre}
                                  {a.id_miembro && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-normal">Miembro</span>}
                                  {!a.activo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-normal">De baja</span>}
                                </td>
                                <td className="px-3 py-2 text-slate-600">{a.iglesia || "—"}</td>
                                <td className="px-3 py-2 text-slate-600">
                                  {a.tarifa}
                                  {a.detalle_cuota && <span className="block text-xs text-slate-400">{a.detalle_cuota}</span>}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{formatoMoneda(a.cuota)}</td>
                                <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{formatoMoneda(a.beca)}</td>
                                <td className="px-3 py-2 text-right text-slate-800 tabular-nums">{formatoMoneda(a.abonado)}</td>
                                <td className={`px-3 py-2 text-right font-medium tabular-nums ${a.saldo > 0 ? "text-amber-600" : "text-slate-400"}`}>{formatoMoneda(a.saldo)}</td>
                                <td className="px-3 py-2"><span className={`px-2 py-1 text-xs rounded-full font-medium ${est.estilo}`}>{est.etiqueta}</span></td>
                                <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={!!a.formulario_firmado}
                                    disabled={!a.activo || inactivo}
                                    onChange={(e) => alternarFormulario(a, e.target.checked)}
                                    aria-label={`Formulario firmado de ${a.nombre}`}
                                    className="w-4 h-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
                                  />
                                </td>
                              </tr>
                              {abierto && (
                                <tr className="border-t border-slate-100 bg-slate-50/60">
                                  <td colSpan={11} className="px-4 py-4">
                                    {editandoAsistente?.id_asistente === a.id_asistente ? (
                                      <FormAsistente
                                        editando
                                        miembros={miembros}
                                        tarifas={tarifasParaElegir}
                                        guardando={guardando}
                                        inicial={{
                                          nombre: a.nombre, iglesia: a.iglesia, beca: a.beca, id_miembro: a.id_miembro,
                                          tarifa: a.id_tarifa ?? PERSONALIZADA, cuota_personalizada: a.cuota_personalizada, detalle_cuota: a.detalle_cuota, formulario_firmado: a.formulario_firmado,
                                        }}
                                        onCancelar={() => setEditandoAsistente(null)}
                                        onGuardar={guardarEdicionAsistente}
                                      />
                                    ) : (
                                      <div className="grid md:grid-cols-2 gap-5">
                                        <div>
                                          <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">Abonos de {a.nombre}</h4>
                                          {a.abonos.length === 0 ? (
                                            <p className="text-sm text-slate-400">Todavía no ha abonado nada.</p>
                                          ) : (
                                            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
                                              {a.abonos.map((ab) => (
                                                <li key={ab.id_abono} className="flex items-center gap-3 px-3 py-2 text-sm">
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-slate-800 font-medium tabular-nums">{formatoMoneda(ab.monto)}</p>
                                                    <p className="text-xs text-slate-400 truncate">
                                                      {formatoFecha(ab.fecha)} · {FORMAS_PAGO.find((f) => f.value === ab.forma_pago)?.label || ab.forma_pago}{ab.nota ? ` · ${ab.nota}` : ""}
                                                    </p>
                                                  </div>
                                                  {a.activo && !inactivo && (
                                                    <button type="button" onClick={() => setAbonoACancelar({ ...ab, nombre: a.nombre })} aria-label="Cancelar abono" title="Cancelar abono" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                                      <FaTrashAlt className="text-xs" />
                                                    </button>
                                                  )}
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                          <p className="text-xs text-slate-500 mt-2">
                                            Debe {formatoMoneda(a.neto)} (cuota {formatoMoneda(a.cuota)} − beca {formatoMoneda(a.beca)}) · Abonado {formatoMoneda(a.abonado)} · Saldo <b>{formatoMoneda(a.saldo)}</b>
                                          </p>
                                        </div>

                                        <div>
                                          {a.activo && !inactivo && a.saldo > 0 ? (
                                            <>
                                              <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">Registrar abono</h4>
                                              <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                  <label className="block text-xs text-slate-600 mb-1">Monto</label>
                                                  <input type="number" min="0" step="0.01" value={abonoForm.monto} onChange={(e) => setAbonoForm({ ...abonoForm, monto: e.target.value })} placeholder="0.00" className={clasesInput} />
                                                </div>
                                                <div>
                                                  <label className="block text-xs text-slate-600 mb-1">Fecha</label>
                                                  <input type="date" max={hoyMexico()} value={abonoForm.fecha} onChange={(e) => setAbonoForm({ ...abonoForm, fecha: e.target.value })} className={clasesInput} />
                                                </div>
                                                <div>
                                                  <label className="block text-xs text-slate-600 mb-1">Forma de pago</label>
                                                  <select value={abonoForm.forma_pago} onChange={(e) => setAbonoForm({ ...abonoForm, forma_pago: e.target.value })} className={clasesInput}>
                                                    {FORMAS_PAGO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs text-slate-600 mb-1">Nota (opcional)</label>
                                                  <input type="text" maxLength={200} value={abonoForm.nota} onChange={(e) => setAbonoForm({ ...abonoForm, nota: e.target.value })} className={clasesInput} />
                                                </div>
                                              </div>
                                              <div className="flex flex-wrap gap-2 mt-3">
                                                <button type="button" onClick={() => registrarAbono(a)} disabled={guardando} className={clasesBotonPrimario}>Abonar</button>
                                                <button type="button" onClick={() => setAbonoForm({ ...abonoForm, monto: String(a.saldo) })} className={clasesBotonSecundario}>
                                                  Liquidar ({formatoMoneda(a.saldo)})
                                                </button>
                                              </div>
                                            </>
                                          ) : (
                                            <p className="text-sm text-slate-500">
                                              {!a.activo ? "Este asistente está dado de baja." : a.estatus === "becado" ? "Está becado por completo: no tiene nada que pagar." : a.saldo <= 0 ? "Ya pagó todo lo que debía." : ""}
                                            </p>
                                          )}
                                        </div>

                                        {!inactivo && (
                                          <div className="md:col-span-2 flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                                            {a.activo ? (
                                              <>
                                                <button type="button" onClick={() => setEditandoAsistente(a)} className={`${clasesBotonSecundario} inline-flex items-center gap-2`}>
                                                  <FaPencilAlt className="text-xs" /> Editar asistente
                                                </button>
                                                <button type="button" onClick={() => setBajaPendiente(a)} className={`${clasesBotonSecundario} inline-flex items-center gap-2 hover:text-rose-600`}>
                                                  <FaUserMinus className="text-xs" /> Dar de baja
                                                </button>
                                              </>
                                            ) : (
                                              <button type="button" onClick={() => cambiarEstatusAsistente(a, true)} className={`${clasesBotonSecundario} inline-flex items-center gap-2`}>
                                                <FaUserCheck className="text-xs" /> Reactivar asistente
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </FilaAsistente>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {!esNuevo && bajas > 0 && (
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                  <input type="checkbox" checked={verBajas} onChange={(e) => setVerBajas(e.target.checked)} />
                  Mostrar asistentes dados de baja ({bajas})
                </label>
              )}
            </section>

            {error && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</p>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button type="button" onClick={onClose} className={clasesBotonSecundario}>{esNuevo ? "Cancelar" : "Cerrar"}</button>
              {!inactivo && (
                <button type="button" onClick={guardarCampamento} disabled={guardando} className={clasesBotonPrimario}>
                  {guardando ? "Guardando..." : esNuevo ? "Crear campamento" : "Guardar cambios"}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* CONFIRMAR BAJA DE ASISTENTE */}
      <Modal open={!!bajaPendiente} onClose={() => setBajaPendiente(null)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-rose-50 text-rose-500"><FaExclamationTriangle /></div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Dar de baja</h3>
          <p className="text-sm text-slate-500 mb-6 break-words">
            ¿Dar de baja a {bajaPendiente?.nombre}? Dejará de contar en los totales. Sus abonos{bajaPendiente?.abonado > 0 ? ` (${formatoMoneda(bajaPendiente.abonado)})` : ""} se conservan y podrás reactivarlo.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setBajaPendiente(null)} className={`${clasesBotonSecundario} w-full`}>Cancelar</button>
            <button onClick={() => cambiarEstatusAsistente(bajaPendiente, false)} className="w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition bg-rose-600 hover:bg-rose-700">Confirmar</button>
          </div>
        </div>
      </Modal>

      {/* CONFIRMAR CANCELAR ABONO */}
      <Modal open={!!abonoACancelar} onClose={() => setAbonoACancelar(null)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-rose-50 text-rose-500"><FaExclamationTriangle /></div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Cancelar abono</h3>
          <p className="text-sm text-slate-500 mb-6">
            ¿Cancelar el abono de {abonoACancelar && formatoMoneda(abonoACancelar.monto)} de {abonoACancelar?.nombre}? El saldo pendiente aumentará y la cancelación queda en el historial.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setAbonoACancelar(null)} className={`${clasesBotonSecundario} w-full`}>No, conservar</button>
            <button onClick={cancelarAbono} className="w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition bg-rose-600 hover:bg-rose-700">Cancelar abono</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// Agrupa la fila de un asistente con su detalle desplegable dentro de un <tbody>.
function FilaAsistente({ children }) {
  return <>{children}</>;
}
